// MyDepositPanel — Phase 4 deposit view inside the consolidated
// /profile hub.
//
// Structure (top-to-bottom):
//   1. Indigo hero card  — animated count-up of deposit balance +
//                          margin status badge (Safe/Warning/Locked)
//   2. Coverage gauge    — horizontal bar showing how much of the
//                          deposit is "consumed" by a negative pool,
//                          with 80% (warning) and 100% (lockout)
//                          thresholds marked. Same colour mental model
//                          as EmergencyLockoutGate + MarginWarningModal
//                          (emerald/amber/rose).
//   3. Deposit ledger    — single-column rows of admin credits +
//                          refunds, chronological.

import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { useCountUp } from '@baaligames/shared-ui/admin/lib/useCountUp';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
// Phase 10 (2026-05-12): the deposit panel now has its own
// recharge + withdrawal forms, both `target='deposit'` versions of
// the same components MyWalletPanel uses for the operating pool.
import RequestRechargeForm from '../../components/RequestRechargeForm';
import RequestWithdrawalForm from '../../components/RequestWithdrawalForm';

type DepositLedgerRow = {
  id: number; kind: string; amount: number;
  source_ref: string | null; created_at: string;
};

type MarginStatus = 'safe' | 'warning' | 'locked' | undefined;

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

export default function MyDepositPanel() {
  const { admin } = useAdminAuth();
  const t = useT();
  const { balance: poolBalance } = useAgentWallet();
  const depositBalance = Number((admin as any)?.depositBalance ?? 0);
  const marginStatus = ((admin as any)?.marginStatus as MarginStatus) ?? 'safe';

  const [rows, setRows] = useState<DepositLedgerRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<'none' | 'recharge' | 'withdrawal'>('none');

  const loadLedger = useCallback(async () => {
    try {
      const r = await adminApi.get<{ rows: DepositLedgerRow[] }>('/admin/me-agent/deposit/ledger?limit=200');
      setRows(r.rows);
    } catch (e) { setErr((e as ApiError).message); }
  }, []);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const onSubmitted = useCallback(() => {
    setOpenForm('none');
    loadLedger();
  }, [loadLedger]);

  return (
    <div className="space-y-4">
      <HeroDepositCard deposit={depositBalance} status={marginStatus} />

      {/* Phase 10: deposit actions. Recharge tops up the deposit
          via the same dual-QR flow as the wallet recharge (parent
          QR + UPI/Cash toggle). Withdraw pulls from the deposit
          balance — held immediately, paid by the parent later. */}
      <div className="grid grid-cols-2 gap-2">
        <ActionButton
          icon="arrow_upward"
          label={t('agent.deposit.action.recharge')}
          active={openForm === 'recharge'}
          onClick={() => setOpenForm(openForm === 'recharge' ? 'none' : 'recharge')}
        />
        <ActionButton
          icon="arrow_downward"
          label={t('agent.deposit.action.withdraw')}
          active={openForm === 'withdrawal'}
          onClick={() => setOpenForm(openForm === 'withdrawal' ? 'none' : 'withdrawal')}
        />
      </div>
      <AnimatePresence initial={false}>
        {openForm === 'recharge' && (
          <RequestRechargeForm
            key="deposit-recharge"
            target="deposit"
            onSubmitted={onSubmitted}
            onCancel={() => setOpenForm('none')}
          />
        )}
        {openForm === 'withdrawal' && (
          <RequestWithdrawalForm
            key="deposit-withdrawal"
            target="deposit"
            balance={depositBalance}
            onSubmitted={onSubmitted}
            onCancel={() => setOpenForm('none')}
          />
        )}
      </AnimatePresence>

      <CoverageGauge pool={poolBalance} deposit={depositBalance} status={marginStatus} />
      <DepositLedger rows={rows} err={err} />
    </div>
  );
}

function ActionButton({
  icon, label, active, onClick,
}: {
  icon: string; label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-2xl px-4 py-3
                  text-[13px] font-bold transition active:scale-[0.98] border
                  ${active
                    ? 'bg-amber-500 text-white border-amber-500 shadow-[0_2px_10px_rgba(245,158,11,0.45)]'
                    : 'bg-white dark:bg-arena-700/40 text-arena-800 dark:text-white border-arena-200/60 dark:border-arena-600/50 hover:bg-arena-50/40 dark:hover:bg-arena-700/55'}`}
    >
      <Icon name={icon} size={16} />
      {label}
    </button>
  );
}

// ─── Hero card ────────────────────────────────────────────────────

function HeroDepositCard({ deposit, status }: { deposit: number; status: MarginStatus }) {
  const t = useT();
  const animated = useCountUp(deposit);
  const display = Math.round(animated);
  // Phase R7 follow-up (2026-05-14): zero-deposit triggers the
  // shamed-face indicator inside the hero (next to the amount) AND
  // on the bottom-nav Profile tab AND on the ProfileHub Deposit
  // sub-tab. The agent sees the same 😳 at every surface, so the
  // alert→cause chain is unbroken regardless of where they look.
  const isZero = deposit === 0;

  return (
    // U1 (2026-07-09): plain <section>, no JS opacity entrance — the hero
    // must be readable from first paint even when rAF is throttled.
    <section
      className="relative overflow-hidden rounded-2xl p-5
                 bg-gradient-to-br from-brand-500 to-brand-700
                 ring-1 ring-brand-300/40
                 shadow-[0_10px_30px_-12px_rgba(var(--brand-rgb),0.55)]
                 text-white"
    >
      <div aria-hidden className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
      <div className="relative">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <Icon name="account_balance" size={16} className="opacity-90" />
            <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold opacity-90">
              {t('agent.deposit.hero.label')}
            </span>
          </div>
          <MarginBadge status={status} />
        </div>
        <div className="font-display font-black text-4xl sm:text-5xl tabular-nums leading-none
                        flex items-center gap-3 flex-wrap">
          <span>₹{display.toLocaleString('en-IN')}</span>
          {isZero && (
            <motion.span
              initial={{ scale: 0.6, opacity: 0, rotate: 0 }}
              animate={{
                opacity: 1,
                scale:  [1, 1.14, 0.96, 1.10, 1],
                rotate: [0, 10, -10, 5, -5, 0],
              }}
              transition={{
                opacity: { duration: 0.25 },
                scale:  { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
                rotate: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
              }}
              aria-hidden="true"
              className="text-3xl sm:text-4xl leading-none select-none
                         drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]"
            >
              😳
            </motion.span>
          )}
        </div>
        <p className="text-[12.5px] mt-2 text-white/85 leading-snug max-w-md">
          {t('agent.deposit.hero.helper')}
        </p>
      </div>
    </section>
  );
}

function MarginBadge({ status }: { status: MarginStatus }) {
  const t = useT();
  if (!status || status === 'safe') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold
                       bg-emerald-500 text-white shadow-[0_2px_8px_rgba(16,185,129,0.4)]">
        <span className="w-1.5 h-1.5 rounded-full bg-white" />
        {t('agent.deposit.margin.safe')}
      </span>
    );
  }
  if (status === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold
                       bg-amber-400 text-amber-950 shadow-[0_2px_8px_rgba(251,191,36,0.5)]">
        {t('agent.deposit.margin.warning')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold
                     bg-rose-500 text-white shadow-[0_2px_8px_rgba(244,63,94,0.5)]">
      {t('agent.deposit.margin.locked')}
    </span>
  );
}

// ─── Coverage gauge ──────────────────────────────────────────────

function CoverageGauge({
  pool, deposit, status,
}: {
  pool: number; deposit: number; status: MarginStatus;
}) {
  const t = useT();
  // How much of the deposit collateral is "consumed" by a negative
  // pool balance. Wallet positive → 0% consumed. Wallet -₹N → N/deposit.
  //
  // Phase 10 follow-up (2026-05-12): edge case — when deposit = 0
  // but the wallet has gone negative, the old formula collapsed to
  // 0/0 → 0% green. That hides a real breach. With zero cushion the
  // gauge should read full red, so we treat consumed-without-deposit
  // as 100% consumed.
  const consumed = Math.max(0, -pool);
  const ratio =
    deposit > 0
      ? Math.min(1, consumed / deposit)
      : (consumed > 0 ? 1 : 0);
  const pct = Math.round(ratio * 100);

  const tone = status === 'locked' ? 'rose'
             : status === 'warning' ? 'amber'
             : 'emerald';
  const fillCls = tone === 'rose'
    ? 'bg-gradient-to-r from-rose-500 to-rose-600'
    : tone === 'amber'
      ? 'bg-gradient-to-r from-amber-400 to-amber-500'
      : 'bg-gradient-to-r from-emerald-500 to-emerald-600';
  const captionCls = tone === 'rose'
    ? 'text-rose-700 dark:text-rose-300'
    : tone === 'amber'
      ? 'text-amber-700 dark:text-amber-300'
      : 'text-emerald-700 dark:text-emerald-300';

  const caption =
    status === 'locked'  ? t('agent.deposit.gauge.caption.locked')
  : status === 'warning' ? t('agent.deposit.gauge.caption.warning', { pct })
  :                        consumed > 0
                              ? t('agent.deposit.gauge.caption.caution', { pct })
                              : t('agent.deposit.gauge.caption.safe');

  return (
    <section
      className="rounded-2xl p-4
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon name="speed" size={16} className="text-brand-600 dark:text-brand-300" />
        <span className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/80">
          {t('agent.deposit.gauge.label')}
        </span>
        <span className="ml-auto font-mono tabular-nums text-[13px] font-bold text-arena-900 dark:text-white">
          {pct}%
        </span>
      </div>

      {/* Bar */}
      <div className="relative h-3 rounded-full bg-arena-100/80 dark:bg-arena-900/60 overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className={`h-full rounded-full ${fillCls}`}
        />
        {/* 80% warning marker */}
        <div className="absolute top-0 bottom-0 w-px bg-amber-500/60 dark:bg-amber-300/70" style={{ left: '80%' }} />
        {/* 100% lockout marker */}
        <div className="absolute top-0 bottom-0 w-px bg-rose-500/60 dark:bg-rose-300/70" style={{ left: 'calc(100% - 1px)' }} />
      </div>

      {/* Threshold labels — on phones we show only the percentages
          ("0%", "80%", "100%") so the labels never collide at narrow
          widths; at sm+ the descriptive suffix ("warn" / "lock")
          reappears for fuller context. The 100% label is right-aligned
          (right-0) — the 80% label is RIGHT-ALIGNED at the 80% mark
          rather than centred over it, so its rightmost edge sits
          exactly on the tick and can never overlap the next label
          regardless of viewport width. */}
      <div className="relative mt-1.5 text-[10px] text-arena-500 dark:text-neon-200/70">
        <span className="absolute left-0">0%</span>
        <span
          className="absolute whitespace-nowrap"
          style={{ left: '80%', transform: 'translateX(-100%)', paddingRight: 4 }}
        >
          80%<span className="hidden sm:inline"> {t('agent.deposit.gauge.thresholdWarn')}</span>
        </span>
        <span className="absolute right-0 whitespace-nowrap">
          100%<span className="hidden sm:inline"> {t('agent.deposit.gauge.thresholdLock')}</span>
        </span>
        {/* Spacer to give the absolute children a row height */}
        <span className="invisible">.</span>
      </div>

      {/* Threshold legend — spells out what 80% and 100% mean, in
          words the agent can read at a glance. The colored dots match
          the amber + rose tick marks on the bar so the eye can
          correlate "what colour is that tick on the bar?" → "what
          does it actually do?". flex-wrap so on tiny phones each
          line drops to its own row instead of squishing.
          NOTE: the {pct} placeholder lets each language inject its
          own bold/strong-tag treatment inline — we don't try to
          slice the HTML here because each script may want a
          different break point. */}
      <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5 text-[11.5px]">
        <span className="inline-flex items-center gap-1.5 text-arena-700 dark:text-neon-200/85">
          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
          {t('agent.deposit.gauge.legend.warn', { pct: '80%' })}
        </span>
        <span className="inline-flex items-center gap-1.5 text-arena-700 dark:text-neon-200/85">
          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
          {t('agent.deposit.gauge.legend.lock', { pct: '100%' })}
        </span>
      </div>

      <p className={`mt-3 text-[12.5px] leading-snug ${captionCls}`}>
        {caption}
      </p>
    </section>
  );
}

// ─── Ledger ─────────────────────────────────────────────────────

function DepositLedger({ rows, err }: { rows: DepositLedgerRow[] | null; err: string | null }) {
  const t = useT();
  if (err) {
    return (
      <div className="rounded-2xl bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-400/30 border text-rose-800 dark:text-rose-200 px-4 py-3 text-[13px]">
        {err}
      </div>
    );
  }
  if (!rows) {
    return (
      <CardShell>
        <div className="px-4 py-6 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
          {t('agent.deposit.ledger.loading')}
        </div>
      </CardShell>
    );
  }
  if (rows.length === 0) {
    return (
      <CardShell>
        <div className="px-4 py-8 text-center">
          <Icon name="receipt_long" size={28} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
          <div className="text-[13px] text-arena-900 dark:text-white font-bold">{t('agent.deposit.ledger.empty.title')}</div>
          <div className="text-[11.5px] text-arena-600 dark:text-neon-200/75 mt-1 max-w-sm mx-auto">
            {t('agent.deposit.ledger.empty.body')}
          </div>
        </div>
      </CardShell>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-2.5">
      {rows.map((r) => (
        <li key={r.id}>
          <DepositRow row={r} />
        </li>
      ))}
    </ul>
  );
}

function DepositRow({ row }: { row: DepositLedgerRow }) {
  const credit = row.amount >= 0;
  const label = row.kind.replace(/_/g, ' ');
  return (
    <CardShell>
      <div className="flex items-center gap-3 p-3.5">
        <span className={`grid place-items-center w-9 h-9 rounded-xl ring-1 shrink-0
                          ${credit
                            ? 'bg-emerald-50 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-400/30'
                            : 'bg-rose-50 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-400/30'}`}>
          <Icon name={credit ? 'add_circle' : 'remove_circle'} size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-arena-900 dark:text-white capitalize truncate">
            {label}
          </div>
          {row.source_ref && (
            <div className="text-[11px] text-arena-600 dark:text-neon-200/75 font-mono truncate">
              {row.source_ref}
            </div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/70 mt-0.5">
            {fmtDate(row.created_at)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-mono font-bold tabular-nums text-[14px]
                            ${credit ? 'text-emerald-700 dark:text-emerald-300'
                                     : 'text-rose-700 dark:text-rose-300'}`}>
            {credit ? '+' : '−'}{inr(Math.abs(row.amount))}
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  // U1 (2026-07-09): no JS opacity entrance — content readable from first paint.
  return (
    <div
      className="rounded-2xl
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      {children}
    </div>
  );
}
